<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { inject, ref, watch, onMounted, computed } from "vue";
import { z } from "zod";

import {
  EDurationUnit,
  EProgrammingDurationMode,
  IPilotingWizardState,
  PilotingWizardStateInjectKey,
  getTrackingDurationMode,
} from "@/components/piloting/wizard/piloting-wizard.types";
import { IWizardInject, WizardInject } from "@/components/ui/wizard/wizard.types";
import { useApplyPlanningOverridesBySection } from "@/composables/kili/internal/mutations/use-apply-planning-overrides-by-section";
import { useToaster } from "@/composables/layout/use-toaster";
import {
  TApplyPlanningOverrideRange,
  TSectionPlanning,
  TPlanningOverrideRange,
  TPlanningOverrideHeaterRangeTemperature,
} from "@/data/api/kili/sites/types/planning.type";
import { TSite } from "@/data/api/kili/sites/types/sites.type";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import {
  EHeaterProgramMetaMode,
  EOnProgramMetaMode,
  TProgramMetaMode,
  ECommonProgramMetaMode,
  EBookableSpaceMetaMode,
  EProgrammingMetaMode,
} from "@/domains/v4b/program/subprogram.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import $date, { EDateScaling } from "@/utils/date";
import { catchError } from "@/utils/errors";
import { getMetaModeGroupInstructions } from "@/utils/programs/program-utils";
import { getModeEquivalentForNotFR } from "@/utils/translation-utils";

import Divider from "primevue/divider";
import InputNumber from "primevue/inputnumber";

import PilotingModeCardButton from "@/components/piloting/piloting-mode-card-button.vue";
import PilotingDateRangeInputs from "@/components/piloting/wizard/piloting-date-range-inputs.vue";
import PilotingDurationInputs from "@/components/piloting/wizard/piloting-duration-inputs.vue";
import PilotingTimezoneMessage from "@/components/piloting/wizard/piloting-timezone-message.vue";
import HorizontalMenu, { THorizontalMenuItem } from "@/components/ui/horizontal-menu/horizontal-menu.vue";

interface IProps {
  site?: TSite;
  selectedApplianceCategory: EProgramApplianceCategory;
}

interface IEmits {
  (e: "refresh-plannings"): void;
}

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();
const { t, k } = useTypedIntl();
const wizard = inject<IWizardInject>(WizardInject)!;

const applyDateRangeSchema = z.object({
  durationMode: z.literal(EProgrammingDurationMode.DATE_RANGE),
  fromDate: z.date(),
  toDate: z.date(),
});

const applyDurationSchema = z.object({
  durationMode: z.literal(EProgrammingDurationMode.DURATION),
  unit: z.union([
    z.literal(EDurationUnit.HOUR),
    z.literal(EDurationUnit.DAY),
    z.literal(EDurationUnit.WEEK),
    z.literal(EDurationUnit.MONTH),
  ]),
  amount: z.number().gt(0, t(k.veeValidate.messages.min_value({ min: 1 }))),
});

const temperatureModeSchema = z.object({
  selectedMode: z.literal(ECommonProgramMetaMode.TEMP),
  temperature: z.number().gt(0, t(k.veeValidate.messages.min_value({ min: 1 }))),
});

const comfortModeSchema = z.object({
  selectedMode: z.literal(EHeaterProgramMetaMode.COMFORT),
});
const ecoModeSchema = z.object({
  selectedMode: z.literal(EHeaterProgramMetaMode.ECO),
});
const noFrostModeSchema = z.object({
  selectedMode: z.literal(EHeaterProgramMetaMode.NO_FROST),
});
const offModeSchema = z.object({
  selectedMode: z.literal(ECommonProgramMetaMode.OFF),
});
const onModeSchema = z.object({
  selectedMode: z.literal(EOnProgramMetaMode.ON),
});
const availableModeSchema = z.object({
  selectedMode: z.literal(EBookableSpaceMetaMode.AVAILABLE),
});
const occupiedModeSchema = z.object({
  selectedMode: z.literal(EBookableSpaceMetaMode.OCCUPIED),
});

const formSchema = z.object({
  mode: z.discriminatedUnion("selectedMode", [
    temperatureModeSchema,
    comfortModeSchema,
    ecoModeSchema,
    noFrostModeSchema,
    offModeSchema,
    onModeSchema,
    availableModeSchema,
    occupiedModeSchema,
  ]),
  apply: z.discriminatedUnion("durationMode", [applyDateRangeSchema, applyDurationSchema]),
});

const { values, handleSubmit, errors, defineField, resetField, setFieldValue } = useForm({
  validationSchema: toTypedSchema(formSchema),
  initialValues: {
    mode: {
      selectedMode: EHeaterProgramMetaMode.COMFORT,
    },
    apply: {
      durationMode: EProgrammingDurationMode.DATE_RANGE,
      fromDate: new Date(),
      toDate: $date.add(new Date(), 1, EDateScaling.DAY),
    },
  },
});

const [selectedMode] = defineField("mode.selectedMode");
const [temperature] = defineField("mode.temperature");
const [durationMode] = defineField("apply.durationMode");
const [fromDate] = defineField("apply.fromDate");
const [toDate] = defineField("apply.toDate");
const [amount] = defineField("apply.amount");
const [unit] = defineField("apply.unit");

const toast = useToaster();
const { mutateAsync: applyPlanningOverridesBySection } = useApplyPlanningOverridesBySection();

watch(selectedMode, (nextVal) => {
  if (nextVal !== ECommonProgramMetaMode.TEMP) {
    resetField("mode.temperature");
    return;
  }

  if (state.formMode === "edit" && state.maybeSelectedSchedule?.initialSchedule.temperature) {
    temperature.value = state.maybeSelectedSchedule.initialSchedule.temperature;
    return;
  }

  temperature.value = 17;
});

watch(durationMode, (nextVal) => {
  if (nextVal === EProgrammingDurationMode.DATE_RANGE) {
    if (state.formMode === "edit") return;

    fromDate.value = new Date();
    toDate.value = $date.add(new Date(), 1, EDateScaling.DAY);
  } else {
    unit.value = EDurationUnit.HOUR;
    amount.value = 1;
  }
});

const state = inject<IPilotingWizardState>(PilotingWizardStateInjectKey)!;

const modeNavItems = ref<[THorizontalMenuItem, ...THorizontalMenuItem[]]>([
  {
    id: EProgrammingDurationMode.DATE_RANGE,
    label: t(k.wizard.piloting.programmingDurationMode[EProgrammingDurationMode.DATE_RANGE]),
    value: EProgrammingDurationMode.DATE_RANGE,
  },
  {
    id: EProgrammingDurationMode.DURATION,
    label: t(k.wizard.piloting.programmingDurationMode[EProgrammingDurationMode.DURATION]),
    value: EProgrammingDurationMode.DURATION,
  },
]);

const availableInstructions = computed<Exclude<TProgramMetaMode, "PROGRAMMING">[]>(() => {
  return getMetaModeGroupInstructions(props.selectedApplianceCategory);
});

function mergeAdjacentRanges(ranges: TPlanningOverrideRange[]): TApplyPlanningOverrideRange[] {
  if (ranges.length === 0) return [];

  ranges.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

  const merged: TPlanningOverrideRange[] = [ranges[0]];

  for (let i = 1; i < ranges.length; i++) {
    const current = ranges[i];
    const lastMerged = merged[merged.length - 1];

    const canMerge =
      current.metaMode === lastMerged.metaMode &&
      (current.metaMode !== ECommonProgramMetaMode.TEMP ||
        current.temperature === ((lastMerged as TPlanningOverrideHeaterRangeTemperature)?.temperature ?? -1)) &&
      current.startTime.getTime() <= lastMerged.endTime.getTime();

    if (canMerge) {
      lastMerged.endTime = new Date(Math.max(lastMerged.endTime.getTime(), current.endTime.getTime()));
    } else {
      merged.push(current);
    }
  }

  return merged
    .map((k) => {
      return {
        ...k,
        startTime: $date.formatDateWithoutTimezone(k.startTime),
        endTime: $date.formatDateWithoutTimezone(k.endTime),
      };
    })
    .filter((k) => {
      return k.startTime !== k.endTime;
    });
}

function insertDateRange(
  ranges: Array<TPlanningOverrideRange>,
  newRange: TPlanningOverrideRange,
): Array<TApplyPlanningOverrideRange> {
  const result: TPlanningOverrideRange[] = [];

  ranges.forEach((range) => {
    if (range.endTime < newRange.startTime || range.startTime > newRange.endTime) {
      // No collisions
      result.push(range);
      return;
    }

    if (range.startTime > newRange.startTime && range.endTime < newRange.endTime) {
      // We totally overlap existing range, so we erase existing range
      return;
    }

    if (range.startTime < newRange.startTime && range.endTime > newRange.endTime) {
      // We are inside an existing range, we split into two pieces
      result.push({
        ...range,
        startTime: range.startTime,
        endTime: new Date(newRange.startTime.getTime()),
      });
      result.push({
        ...range,
        startTime: new Date(newRange.endTime.getTime()),
        endTime: range.endTime,
      });

      return;
    }

    if (
      range.startTime < newRange.startTime &&
      range.endTime > newRange.startTime &&
      range.endTime < newRange.endTime
    ) {
      // New range is in the right side, so we shortened existing range to match the new range start time
      result.push({
        ...range,
        startTime: range.startTime,
        endTime: new Date(newRange.startTime.getTime()),
      });
      return;
    }

    if (range.startTime < newRange.endTime && range.endTime > newRange.startTime && range.endTime > newRange.endTime) {
      // New range is in the left side, so we shortened existing range to match the new range end time
      result.push({
        ...range,
        startTime: new Date(newRange.endTime.getTime()),
        endTime: range.endTime,
      });
    }

    // Else ? We probably do not want to insert anything that is not covered by the upper code
  });

  result.push(newRange);

  return mergeAdjacentRanges(result);
}

function buildNextPlanning(
  planning: TSectionPlanning,
  startTime: Date,
  endTime: Date,
): Array<TApplyPlanningOverrideRange> {
  if (!planning.ranges || !planning.ranges.length) {
    return [
      {
        startTime: $date.formatDateWithoutTimezone(startTime),
        endTime: $date.formatDateWithoutTimezone(endTime),
        metaMode: selectedMode.value!,
        temperature: temperature.value!,
      },
    ];
  }

  const initialHeaterSchedules = planning.ranges ?? [];
  const results: Array<TPlanningOverrideRange> = [...initialHeaterSchedules];

  return insertDateRange(results, {
    startTime: startTime,
    endTime: endTime,
    metaMode: selectedMode.value!,
    temperature: temperature.value!,
  });
}

const startDate = computed<Date | undefined>(() => {
  const maybeApply = values.apply;

  if (!maybeApply) return;

  if (maybeApply.durationMode === EProgrammingDurationMode.DATE_RANGE) {
    return maybeApply.fromDate;
  } else if (maybeApply.durationMode === EProgrammingDurationMode.DURATION) {
    if (state.formMode === "create") {
      return $date.startOf(new Date(), EDateScaling.DAY);
    } else if (state.formMode === "edit" && state.maybeSelectedSchedule) {
      const selectedSchedule = state.maybeSelectedSchedule.initialSchedule;
      return $date.startOf(selectedSchedule.realStart, EDateScaling.DAY);
    }
  }

  return undefined;
});

const endDate = computed<Date | undefined>(() => {
  const maybeApply = values.apply;

  if (!maybeApply) return;

  if (maybeApply.durationMode === EProgrammingDurationMode.DATE_RANGE) {
    return maybeApply.toDate;
  } else if (maybeApply.durationMode === EProgrammingDurationMode.DURATION && maybeApply.amount) {
    if (!startDate.value) return undefined;

    switch (maybeApply.unit) {
      case EDurationUnit.HOUR:
        return $date.add(startDate.value, maybeApply.amount, EDateScaling.HOURS);
      case EDurationUnit.DAY:
        return $date.endOf($date.add(startDate.value, maybeApply.amount, EDateScaling.DAYS), EDateScaling.DAY);
      case EDurationUnit.WEEK:
        return $date.endOf($date.add(startDate.value, 7 * maybeApply.amount, EDateScaling.DAYS), EDateScaling.DAY);
      case EDurationUnit.MONTH:
        return $date.endOf($date.add(startDate.value, maybeApply.amount, EDateScaling.MONTHS), EDateScaling.DAY);
    }
  }

  return undefined;
});

const submit = handleSubmit(
  async () => {
    if (state.formMode === "create") {
      return await submitCreatePlanningOverridesBySections();
    }

    return await submitEditPlanningOverride();
  },
  () => {
    toast.add({ severity: "error", detail: t(k.global.invalidForm) });
  },
);

async function submitEditPlanningOverride() {
  const startTime: Date | undefined = startDate.value;
  const endTime: Date | undefined = endDate.value;

  if (!state.maybeSelectedSchedule || !startTime || !endTime) return;

  const [err] = await catchError(
    applyPlanningOverridesBySection({
      sectionRanges: [
        {
          sectionId: state.maybeSelectedSchedule.planning.sectionId,
          ranges: buildNextPlanning(state.maybeSelectedSchedule.planning, startTime, endTime),
        },
      ],
      siteId: state.siteId,
      applianceCategory: props.selectedApplianceCategory,
    }),
  );

  if (err) {
    return false;
  }

  toast.add({ severity: "success", detail: t(k.wizard.piloting.messages.edit.success) });

  return true;
}

async function submitCreatePlanningOverridesBySections() {
  const startTime: Date | undefined = startDate.value;
  const endTime: Date | undefined = endDate.value;

  if (!startTime || !endTime) return;

  const plannings = state.plannings.filter(
    (planning) => !!state.selection.find((selec) => selec.id === `${planning.sectionId}`),
  );

  const [err] = await catchError(
    applyPlanningOverridesBySection({
      sectionRanges: plannings.map((planning) => {
        const nextPlanning = buildNextPlanning(planning, startTime, endTime);

        return {
          sectionId: planning.sectionId,
          ranges: nextPlanning,
        };
      }),
      siteId: state.siteId,
      applianceCategory: props.selectedApplianceCategory,
    }),
  );

  catchError(async () => {
    if (!startDate.value || !endDate.value) return;

    MixpanelService.trackEvent(EMixpanelEvent.MANUAL_CONTROL_PLANNED, {
      mode: values.mode?.selectedMode,
      method: getTrackingDurationMode(values.apply?.durationMode),
      duration: $date.diff(endDate.value, startDate.value),
      temperature: values.mode?.selectedMode === ECommonProgramMetaMode.TEMP ? values.mode.temperature : undefined,
    });
  });

  if (err) {
    return false;
  }

  toast.add({ severity: "success", detail: t(k.wizard.piloting.messages.apply.success) });

  return true;
}

async function validate() {
  const response = await submit();

  if (!response) {
    return false;
  }

  // clear sections selection on submit
  state.selection = [];
  emits("refresh-plannings");
  wizard.focusPrevTab();
}

watch(
  availableInstructions,
  (newValue) => {
    if (!newValue.length) return;

    setFieldValue("mode.selectedMode", newValue[0]);
  },
  { immediate: true },
);

watch(
  () => state.formMode,
  (newFormMode) => {
    if (newFormMode === "create" || !state.maybeSelectedSchedule) return;

    const selectedSchedule = state.maybeSelectedSchedule.initialSchedule;

    if (selectedSchedule.type === EProgrammingMetaMode.PROGRAMMING) return;

    selectedMode.value = selectedSchedule.type;

    if (selectedSchedule.type === ECommonProgramMetaMode.TEMP) {
      temperature.value = selectedSchedule.temperature;
    }

    fromDate.value = selectedSchedule.realStart;
    toDate.value = selectedSchedule.realEnd;
  },
  { immediate: true },
);

onMounted(() => {
  MixpanelService.trackEvent(EMixpanelEvent.MANUAL_CONTROL_CONFIGURATION);
});

defineExpose({
  validate,
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <section>
      <PilotingTimezoneMessage
        class="mb-4"
        :site="props.site"
      />

      <div class="mb-3">
        <div class="flex flex-row gap-3">
          <div class="w-[24px] h-[24px] rounded-full flex items-center justify-center bg-neutral-muted text-[14px]">
            1
          </div>
          <h3 class="font-semibold text-[18px] p-0 m-0">
            {{ t(k.wizard.piloting.availableModes) }}
          </h3>
        </div>
        <span class="text-content-muted">
          {{ t(k.wizard.piloting.modeHelpText) }}
        </span>
      </div>
      <div class="grid grid-cols-5 gap-2">
        <PilotingModeCardButton
          v-for="(mode, _index) in availableInstructions"
          :key="_index"
          v-tooltip.top="getModeEquivalentForNotFR(mode, props.site?.siteAddressDto)"
          data-test-id="piloting-mode-item"
          :mode="mode"
          :model-value="selectedMode === mode"
          @click="selectedMode = $event"
        />
      </div>

      <div
        v-if="selectedMode === ECommonProgramMetaMode.TEMP"
        class="flex flex-col mt-2"
      >
        <label
          id="temperature-label"
          aria-label="temperature"
          class="font-medium mb-1"
          for="temperature"
        >
          {{ t(k.wizard.piloting.temperature) }}
        </label>
        <InputNumber
          id="temperature"
          v-model="temperature"
          aria-labelledby="temperature-label"
          :invalid="!!errors['mode.temperature']"
          suffix=" ℃"
        />
        <small class="text-red-500">{{ errors["mode.temperature"] }}</small>
      </div>
    </section>

    <Divider />

    <section>
      <HorizontalMenu
        item-width="166"
        :menu-items="modeNavItems"
        @change="durationMode = $event.value"
      >
        <template #duration>
          <div class="mb-3">
            <div class="flex flex-row gap-3">
              <div class="w-[24px] h-[24px] rounded-full flex items-center justify-center bg-neutral-muted text-[14px]">
                2
              </div>
              <h3 class="font-semibold text-[18px] p-0 m-0">
                {{ t(k.wizard.piloting.duration) }}
              </h3>
            </div>
            <span class="text-content-muted">
              {{ t(k.wizard.piloting.durationHelpText) }}
            </span>
          </div>

          <PilotingDurationInputs
            v-model:amount="amount"
            v-model:unit="unit"
            :amount-error="errors['apply.amount']"
            :unit-error="errors['apply.unit']"
          />
        </template>
        <template #date-range>
          <div class="mb-3">
            <div class="flex flex-row gap-3">
              <div class="w-[24px] h-[24px] rounded-full flex items-center justify-center bg-neutral-muted text-[14px]">
                2
              </div>
              <h3 class="font-semibold text-[18px] p-0 m-0">
                {{ t(k.wizard.piloting.dateRange) }}
              </h3>
            </div>
            <span class="text-content-muted">
              {{ t(k.wizard.piloting.dateRangeHelpText) }}
            </span>
          </div>

          <PilotingDateRangeInputs
            v-model:from-date="fromDate"
            v-model:to-date="toDate"
            :from-date-error="errors['apply.fromDate']"
            :to-date-error="errors['apply.toDate']"
          />
        </template>
      </HorizontalMenu>
    </section>
  </div>
</template>
